import React, { ChangeEvent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  FormProvider,
  ValidatedTextArea,
} from '@tq/ui-components/dist/components/forms-engine';
import { Box, Button, Grid, GridItem, Typography } from '@tq/ui-components';
import { useFormatLocaleDate } from 'config/DateFormats/useFormatLocaleDate';

import { useUpdateAnswer } from 'api';
import { NumberBox } from 'components';
import {
  approveResponseState,
  reasonForLateResponseDetailState,
  reasonForLateResponseIdState,
} from 'store';
import { ConsultationResponseAnswer } from 'types';
import { useResponse } from '../../hooks';

export type ResponseAnswerProps = {
  answer: ConsultationResponseAnswer;
  showEdit: boolean;
};

export const RpcResponseAnswer: React.FC<ResponseAnswerProps> = ({ answer, showEdit }) => {
  const { t } = useTranslation('consultation_response');

  const { id } = useParams();
  const consultationId = +(id ?? 0);

  const { canSubmitIfLateResponse } = useResponse(consultationId);

  const [isApprovingResponse, setIsApprovingResponse] = useRecoilState(approveResponseState);
  const reasonForLateResponseId = useRecoilValue(reasonForLateResponseIdState);
  const reasonForLateResponseDetail = useRecoilValue(reasonForLateResponseDetailState);

  const [isEditing, setIsEditing] = useState(false);
  const [editedAnswer, setEditedAnswer] = useState(answer.answer);

  const answerUpdated = (): void => setIsApprovingResponse(true);

  const { mutate: approveAnswer } = useUpdateAnswer(answerUpdated);
  const formatLocaleDate = useFormatLocaleDate();

  const borderColour = (): string => {
    if (answer.approved) {
      return COLOURS.green[500];
    }
    return isEditing ? 'unset' : COLOURS.gray[300];
  };

  const backgroundColour = (): string => {
    if (answer.approved) {
      return COLOURS.neutral[11];
    }
    return isEditing ? COLOURS.blue[200] : 'unset';
  };

  const questionBackgroundColour = (): string => {
    if (answer.approved) {
      return 'unset';
    }
    return isEditing ? 'unset' : COLOURS.gray[200];
  };

  const lastEditedFontColour = (): string => {
    if (answer.approved) {
      return COLOURS.gray[800];
    }
    return isEditing ? COLOURS.black : COLOURS.blue[600];
  };

  useEffect(() => {
    setIsApprovingResponse(false);
  }, [answer.approved, setIsApprovingResponse]);

  const saveAnswer = (approved: boolean): void => {
    approveAnswer({
      consultationId,
      responseId: answer.responseId,
      reasonForLateResponseId,
      reasonForLateResponseDetail,
      questionId: answer.questionId,
      question: answer.question,
      answerId: answer.id,
      answer: editedAnswer,
      approved,
    });
    setIsApprovingResponse(true);
  };

  const handleApprove = () => {
    saveAnswer(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedAnswer(answer.answer);
  };

  const handleSave = () => {
    saveAnswer(false);
    setIsEditing(false);
  };

  const lastEdited =
    showEdit && answer.dateModified ? (
      <Box fontSize='sm' color={lastEditedFontColour()}>
        {t('last_edited', 'Last edited')} {formatLocaleDate(answer.dateModified)}{' '}
        {t('by', 'By').toLowerCase()} {answer.consulteeName}
      </Box>
    ) : null;

  return (
    <>
      <Grid
        data-testid='answer-card'
        key={answer.id}
        borderRadius={5}
        backgroundColor={backgroundColour()}
        border={`1px solid ${borderColour()}`}
        mb={4}
      >
        <GridItem
          data-testid='answer-card-question'
          borderTopRadius={5}
          backgroundColor={questionBackgroundColour()}
          p={4}
        >
          <Box display={'inline'} mr={3}>
            <strong>{t('question', 'Question')}</strong>
          </Box>
          <NumberBox label={answer.questionNumber} />
          <Typography py={2}>{answer.question}</Typography>
        </GridItem>
        <GridItem
          data-testid='answer-card-answer'
          backgroundColor={answer.approved ? COLOURS.neutral[11] : 'unset'}
          px={4}
          py={{ base: 0, lg: 4 }}
        >
          {isEditing ? (
            <FormProvider>
              <ValidatedTextArea
                data-testid='response-answer-text-area'
                defaultValue={editedAnswer}
                id='answerText'
                label={t('answer_text_label', 'Answer')}
                mode='onChange'
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  setEditedAnswer(event.target.value)
                }
                placeholder={t('answer_text_placeholder', 'Enter your answer here')}
                validation={{
                  required: {
                    value: true,
                    message: t('answer_text_required_error', 'Please answer the question.'),
                  },
                }}
              />
            </FormProvider>
          ) : (
            <>
              <Box
                display={{ base: 'block', lg: 'inline' }}
                mr={3}
                mt={{ base: 4, lg: 'unset' }}
              >
                <strong>{t('answer', 'Answer')}</strong>
              </Box>
              <Typography py={2} maxH={264} overflowX='auto'>
                {editedAnswer}
              </Typography>
              <Box display={{ base: 'block', lg: 'none' }} mb={2}>
                {lastEdited}
              </Box>
            </>
          )}
        </GridItem>
        {showEdit && (
          <GridItem
            data-testid='answer-card-footer'
            borderBottomRadius={5}
            borderTop={`1px solid ${COLOURS.gray[300]}`}
            px={{ base: 0, sm: 4 }}
            py={{ base: 4, sm: 6 }}
          >
            <Grid
              p={{ base: 4, lg: 8 }}
              gridAutoFlow={{ base: 'row', lg: 'column' }}
              w={{ base: '100%', lg: 'unset' }}
              justifyContent={{ base: 'unset', lg: 'space-between' }}
              alignItems='center'
              gridGap={4}
            >
              <GridItem display={{ base: 'none', lg: 'block' }}>{lastEdited}</GridItem>
              <GridItem>
                <Grid
                  gridAutoFlow={{ base: 'row', lg: 'column' }}
                  w={{ base: '100%', lg: 'unset' }}
                  justifyContent={{ base: 'unset', lg: 'space-between' }}
                  gridGap={4}
                >
                  {isEditing ? (
                    <>
                      <Button
                        fontSize='sm'
                        variant='secondary'
                        gridRow={{ base: 2, lg: 1 }}
                        w={{ base: '100%', lg: 'unset' }}
                        onClick={handleCancel}
                      >
                        {t('cancel', 'Cancel')}
                      </Button>
                      <Button
                        fontSize='sm'
                        variant='primary'
                        w={{ base: '100%', lg: 'unset' }}
                        disabled={!canSubmitIfLateResponse}
                        onClick={handleSave}
                      >
                        {t('save', 'Save')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        rightIcon={<FaEdit />}
                        fontSize='sm'
                        variant='secondary'
                        w={{ base: '100%', lg: 'unset' }}
                        onClick={handleEdit}
                      >
                        {t('edit', 'Edit')}
                      </Button>
                      {!answer.approved && !isEditing && (
                        <Button
                          fontSize='sm'
                          variant='primary'
                          w={{ base: '100%', lg: 'unset' }}
                          disabled={!canSubmitIfLateResponse || isApprovingResponse}
                          onClick={handleApprove}
                        >
                          {t('approve_answer', 'Approve answer')}
                        </Button>
                      )}
                    </>
                  )}
                </Grid>
              </GridItem>
            </Grid>
          </GridItem>
        )}
      </Grid>
    </>
  );
};
